/**
 * Client-Side Multi-Factor Proximity Verification (PATENT IMPLEMENTATION)
 * 
 * Verifies user proximity using multiple signals:
 * - Primary: GPS (outdoor) + WiFi BSSID (indoor)
 * - Secondary: Bluetooth device detection
 * 
 * All verification happens locally on device - location history never sent to server
 */

import { getLastLocation } from '../api/location';
import { getWiFiHistory, getBluetoothHistory, getProximityHistory } from './proximityService';

// Verification thresholds
const CONFIG = {
  // GPS verification
  GPS_RADIUS_METERS: 100,        // Match if within 100 meters
  GPS_TIME_WINDOW_MS: 10 * 60 * 1000, // 10 minute window
  
  // WiFi verification
  WIFI_TIME_WINDOW_MS: 15 * 60 * 1000, // 15 minute window (WiFi more stable)
  
  // Bluetooth verification
  BLUETOOTH_TIME_WINDOW_MS: 5 * 60 * 1000, // 5 minute window (shorter range)
  
  // Scoring weights
  WEIGHT_GPS: 0.4,
  WEIGHT_WIFI: 0.4,
  WEIGHT_BLUETOOTH: 0.2,
  
  // Minimum score to verify
  MIN_VERIFICATION_SCORE: 0.5,
};

/**
 * Calculate distance between two GPS coordinates (Haversine formula)
 */
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const toRad = (value) => (value * Math.PI) / 180;
  
  const R = 6371000; // Earth radius in meters
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
    Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in meters
};

/**
 * Get user's location history from localStorage (client-side only)
 */
const getLocalLocationHistory = () => {
  try {
    const history = localStorage.getItem('location_history');
    if (history) {
      return JSON.parse(history);
    }
  } catch (e) {
  }
  return [];
};

/**
 * Save location to local history (client-side only)
 */
export const saveLocationToHistory = (latitude, longitude) => {
  try {
    const history = getLocalLocationHistory();
    const newEntry = {
      latitude,
      longitude,
      timestamp: Date.now()
    };
    
    // Keep last 100 locations (past ~2 hours assuming updates every minute)
    history.push(newEntry);
    if (history.length > 100) {
      history.shift();
    }
    
    localStorage.setItem('location_history', JSON.stringify(history));
  } catch (e) {
  }
};

/**
 * Verify GPS proximity
 */
const verifyGPS = (serverAttrs, localHistory, currentLocation) => {
  const result = {
    matched: false,
    confidence: 0,
    closest_distance: Infinity,
    best_match: null,
  };

  const allLocations = currentLocation ? [...localHistory, currentLocation] : localHistory;
  
  for (const location of allLocations) {
    const distance = calculateDistance(
      serverAttrs.latitude,
      serverAttrs.longitude,
      location.latitude,
      location.longitude
    );
    
    const timeDiff = Math.abs(serverAttrs.timestamp - location.timestamp);
    
    if (distance <= CONFIG.GPS_RADIUS_METERS && timeDiff <= CONFIG.GPS_TIME_WINDOW_MS) {
      result.matched = true;
      
      // Calculate confidence based on distance and time
      const distanceScore = 1 - (distance / CONFIG.GPS_RADIUS_METERS);
      const timeScore = 1 - (timeDiff / CONFIG.GPS_TIME_WINDOW_MS);
      const confidence = (distanceScore * 0.6 + timeScore * 0.4);
      
      if (confidence > result.confidence) {
        result.confidence = confidence;
        result.closest_distance = distance;
        result.best_match = {
          latitude: location.latitude,
          longitude: location.longitude,
          distance_meters: Math.round(distance),
          time_diff_minutes: Math.round(timeDiff / 60000),
        };
      }
    }
  }

  return result;
};

/**
 * Verify WiFi network match
 */
const verifyWiFi = (serverAttrs, wifiHistory) => {
  const result = {
    matched: false,
    confidence: 0,
    matched_network: null,
  };

  // Check if server attributes include WiFi
  if (!serverAttrs.wifi_ssid && !serverAttrs.wifi_bssid) {
    return result;
  }

  const relevantHistory = wifiHistory.filter(
    w => Math.abs(serverAttrs.timestamp - w.timestamp) <= CONFIG.WIFI_TIME_WINDOW_MS
  );

  for (const wifi of relevantHistory) {
    // BSSID match is strongest (unique to access point)
    if (serverAttrs.wifi_bssid && wifi.bssid && 
        serverAttrs.wifi_bssid.toLowerCase() === wifi.bssid.toLowerCase()) {
      result.matched = true;
      result.confidence = 1.0; // BSSID is very reliable
      result.matched_network = { bssid: wifi.bssid, ssid: wifi.ssid };
      break;
    }
    
    // SSID match is weaker (same name, different location possible)
    if (serverAttrs.wifi_ssid && wifi.ssid && 
        serverAttrs.wifi_ssid === wifi.ssid) {
      result.matched = true;
      result.confidence = Math.max(result.confidence, 0.7); // SSID less reliable
      result.matched_network = { ssid: wifi.ssid };
    }
  }

  return result;
};

/**
 * Verify Bluetooth device overlap
 */
const verifyBluetooth = (serverAttrs, bluetoothHistory) => {
  const result = {
    matched: false,
    confidence: 0,
    matched_devices: [],
  };

  // Check if server attributes include Bluetooth
  if (!serverAttrs.bluetooth_devices || serverAttrs.bluetooth_devices.length === 0) {
    return result;
  }

  const relevantHistory = bluetoothHistory.filter(
    b => Math.abs(serverAttrs.timestamp - b.timestamp) <= CONFIG.BLUETOOTH_TIME_WINDOW_MS
  );

  const localDevices = new Set(relevantHistory.map(b => (b.id || b.name).toLowerCase()));
  const serverDevices = serverAttrs.bluetooth_devices.map(d => d.toLowerCase());

  // Find overlapping devices
  const matches = serverDevices.filter(d => localDevices.has(d));

  if (matches.length > 0) {
    result.matched = true;
    // Confidence based on number of matching devices
    result.confidence = Math.min(1.0, matches.length * 0.3 + 0.4);
    result.matched_devices = matches;
  }

  return result;
};

/**
 * PATENT IMPLEMENTATION: Multi-Factor Proximity Verification
 * Verifies attributes locally on device without sending history to server
 * 
 * @param {Object} serverAttributes - Attributes from photo capture
 * @returns {Object} Verification result with breakdown
 */
export const verifyAttributesLocally = async (serverAttributes) => {
  
  // Get all local histories
  const locationHistory = getLocalLocationHistory();
  const wifiHistory = getWiFiHistory();
  const bluetoothHistory = getBluetoothHistory();
  
  
  // Get current location
  let currentLocation = null;
  try {
    const lastLoc = await getLastLocation();
    currentLocation = {
      latitude: lastLoc.latitude,
      longitude: lastLoc.longitude,
      timestamp: new Date(lastLoc.timestamp).getTime()
    };
  } catch (e) {
  }
  
  // Run individual verifications
  const gpsResult = verifyGPS(serverAttributes, locationHistory, currentLocation);
  const wifiResult = verifyWiFi(serverAttributes, wifiHistory);
  const bluetoothResult = verifyBluetooth(serverAttributes, bluetoothHistory);
  
  // Calculate weighted score
  let totalScore = 0;
  let applicableWeight = 0;
  
  // GPS always applicable
  totalScore += gpsResult.confidence * CONFIG.WEIGHT_GPS;
  applicableWeight += CONFIG.WEIGHT_GPS;
  
  // WiFi if data was provided
  if (serverAttributes.wifi_ssid || serverAttributes.wifi_bssid) {
    totalScore += wifiResult.confidence * CONFIG.WEIGHT_WIFI;
    applicableWeight += CONFIG.WEIGHT_WIFI;
  }
  
  // Bluetooth if data was provided
  if (serverAttributes.bluetooth_devices && serverAttributes.bluetooth_devices.length > 0) {
    totalScore += bluetoothResult.confidence * CONFIG.WEIGHT_BLUETOOTH;
    applicableWeight += CONFIG.WEIGHT_BLUETOOTH;
  }
  
  // Normalize score
  const normalizedScore = applicableWeight > 0 ? totalScore / applicableWeight : 0;
  
  // Determine overall match
  // Primary match: GPS OR WiFi must match
  const primaryMatch = gpsResult.matched || wifiResult.matched;
  
  // Overall match: Primary must pass AND score threshold
  const overallMatch = primaryMatch && normalizedScore >= CONFIG.MIN_VERIFICATION_SCORE;
  
  const result = {
    // Legacy fields for compatibility
    location_match: gpsResult.matched,
    time_match: gpsResult.matched, // Time is checked within GPS
    proximity_match: primaryMatch,
    overall_match: overallMatch,
    
    // Detailed breakdown
    verification_details: {
      gps: {
        matched: gpsResult.matched,
        confidence: Math.round(gpsResult.confidence * 100),
        closest_distance_m: gpsResult.closest_distance === Infinity ? null : Math.round(gpsResult.closest_distance),
        best_match: gpsResult.best_match,
      },
      wifi: {
        matched: wifiResult.matched,
        confidence: Math.round(wifiResult.confidence * 100),
        matched_network: wifiResult.matched_network,
        data_available: !!(serverAttributes.wifi_ssid || serverAttributes.wifi_bssid),
      },
      bluetooth: {
        matched: bluetoothResult.matched,
        confidence: Math.round(bluetoothResult.confidence * 100),
        matched_devices: bluetoothResult.matched_devices,
        data_available: !!(serverAttributes.bluetooth_devices && serverAttributes.bluetooth_devices.length > 0),
      },
      overall_score: Math.round(normalizedScore * 100),
      threshold: Math.round(CONFIG.MIN_VERIFICATION_SCORE * 100),
    },
    
    // For legacy compatibility
    local_verification: gpsResult.best_match,
  };
  
  
  return result;
};

/**
 * Auto-verify pending verifications (called periodically)
 */
export const autoVerifyPending = async (pendingVerifications, onVerified) => {
  
  for (const verification of pendingVerifications) {
    try {
      // Verify attributes locally using multi-factor
      const result = await verifyAttributesLocally(verification.attributes);
      
      if (result.overall_match) {
        
        // Callback to confirm verification
        if (onVerified) {
          await onVerified(verification.share_id, result);
        }
      } else {
      }
    } catch (error) {
    }
  }
};
